import { motion } from "framer-motion";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Images from "../../Images";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { TextField } from "@mui/material";
import { forgotPassword } from "../../requests/auth/Authentication";

const validationSchema = Yup.object().shape({
  credential: Yup.string().when("loginType", {
    is: "email",
    then: () =>
      Yup.string().email("Invalid email format").required("Email is required"),
    otherwise: () =>
      Yup.string()
        .required("Phone number is required")
        .matches(/^\+?[1-9]\d{1,14}$/, "Invalid phone number"),
  }),
});

const initialValues = {
  credential: "",
  loginType: "phone",
};

function ForgotPassword() {
  const location = useLocation();
  const loginType = location.state?.loginType || "phone";
  const navigate = useNavigate();

  const forgotPasswordMutate = useMutation(
    forgotPassword,
    {
      onSuccess: (response) => {
        const result = response?.data?.data;

        if (result?.in_error === true) {
          let errorMessage = result?.reason || "Failed to send OTP";
          
          switch (result?.status_code) {
            case "004":
              if (result?.reason === "User cannot be found") {
                errorMessage = "No account found with these credentials. Please check and try again.";
              }
              break;
            default:
              errorMessage = result?.reason || "Something went wrong. Please try again.";
          }
          
          toast.error(errorMessage);
          return;
        }

        // Success case
        toast.success(result?.reason || "OTP sent successfully");
        
        navigate("/password-reset", {
          state: {
            email: result?.data?.email,
            phone: result?.data?.phone_number,
            vendorSlug: result?.data?.vendor_slug
          }
        });
      },
      onError: (error) => {
        const errorResponse = error?.response?.data;
        const errorMessage = errorResponse?.reason 
          || errorResponse?.message 
          || "Unable to send OTP. Please try again later.";
        
        toast.error(errorMessage);
      },
    }
  );

  const handleSubmit = (values) => {    
    forgotPasswordMutate.mutate({
      emailOrPhone: values.credential,
    });
  };

  

  return (
    <div className="h-screen w-full flex flex-col sm:flex-row bg-[#FEFEFE]">
      <motion.div
        className="relative hidden w-full h-full sm:w-1/2 sm:flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#FF6C6C] via-[#FF8B8B] to-[#FF9B9B]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <motion.div
          className="absolute w-[800px] h-[800px] bg-white/10 rounded-full blur-xl"
          initial={{ scale: 0, rotate: 0 }}
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear",
          }}
        />

        <motion.div
          className="absolute top-10 right-10 z-20 p-4 rounded-2xl shadow-lg backdrop-blur-md bg-white/30"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          whileHover={{ scale: 1.05 }}
        >
          <div className="flex gap-3 items-center">
            <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-white">
              Mobile Verification
            </span>
          </div>
        </motion.div>

        <motion.div
          className="absolute left-10 bottom-20 z-20 p-4 rounded-2xl shadow-lg backdrop-blur-md bg-white/30"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 1, duration: 0.8 }}
          whileHover={{ scale: 1.05 }}
        >
          <div className="flex gap-3 items-center">
            <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-white">
              Instant Recovery
            </span>
          </div>
        </motion.div>

        {/* Main Image Container */}
        <motion.div
          className="relative z-10 w-4/5 h-4/5"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          <motion.div
            className="overflow-hidden relative w-full h-full rounded-2xl shadow-2xl backdrop-blur-sm bg-white/10"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            <motion.img
              src={Images.register_third}
              alt="forgot-password"
              className="object-cover w-full h-full rounded-2xl transition-transform duration-700 hover:scale-110"
            />
            <motion.div
              className="flex absolute inset-0 flex-col justify-end items-center pb-20 bg-gradient-to-b from-black/10 via-black/20 to-black/60"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
            >
              <motion.div className="px-8 text-center">
                <motion.h2
                  className="mb-6 text-5xl font-bold text-white drop-shadow-lg"
                  initial={{ y: 20 }}
                  animate={{ y: 0 }}
                  transition={{ delay: 1.2 }}
                >
                  Reset Password
                </motion.h2>
                <motion.p
                  className="mx-auto max-w-md text-xl text-white/90"
                  initial={{ y: 20 }}
                  animate={{ y: 0 }}
                  transition={{ delay: 1.4 }}
                >
                  Secure mobile verification for password recovery
                </motion.p>
              </motion.div>
            </motion.div>
          </motion.div>
        </motion.div>
        <motion.div
          className="absolute right-20 top-1/4 z-10 w-20 h-20 rounded-full border-4 border-white/20"
          animate={{
            y: [0, -20, 0],
            rotate: 360,
          }}
          transition={{
            y: { duration: 2, repeat: Infinity },
            rotate: { duration: 8, repeat: Infinity },
          }}
        />
      </motion.div>

      {/* Right Side */}
      <motion.div
        className="overflow-y-auto px-8 pt-16 pb-8 w-full sm:w-1/2 sm:h-screen sm:px-16"
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Logo */}
        <motion.div
          className="mb-6"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
        >
          <img src={Images.logo} alt="Dealboxx Logo" className="w-32 h-auto" />
        </motion.div>

        <motion.div
          className="mb-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          <motion.button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[#808080] hover:text-[#FF6C6C] transition-colors duration-300"
            whileHover={{ x: -5 }}
            whileTap={{ scale: 0.95 }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span className="font-medium">Back to Login</span>
          </motion.button>
        </motion.div>

        {/* Title and Description */}
        <motion.div
          className="mb-8 space-y-3"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          <h1 className="text-3xl font-bold bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] bg-clip-text text-transparent">
            Forgot Password?
          </h1>
          <p className="text-[#808080] text-base">
            {loginType === "phone"
              ? "Enter your registered mobile number to receive OTP for password reset."
              : "Enter your registered email to receive OTP for password reset."}
          </p>
        </motion.div>

        {/* Form */}
        <Formik
          initialValues={{ ...initialValues, loginType }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ handleSubmit, touched, errors }) => (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.8 }}
            >
              <Form className="space-y-6" onSubmit={handleSubmit}>
                <motion.div whileHover={{ scale: 1.01 }} className="relative">
                  {loginType === "phone" ? (
                    <Field name="credential">
                      {({ field, form }) => (
                        <PhoneInput
                          {...field}
                          country={"gh"}
                          enableSearch={true}
                          inputProps={{
                            name: "credential",
                            required: true,
                          }}
                          inputStyle={{
                            width: "100%",
                            height: "56px",
                            borderRadius: "12px",
                            fontSize: "16px",
                            backgroundColor: "white",
                          }}
                          buttonStyle={{
                            borderTopLeftRadius: "12px",
                            borderBottomLeftRadius: "12px",
                          }}
                          onChange={(value) =>
                            form.setFieldValue("credential", value)
                          }
                        />
                      )}
                    </Field>
                  ) : (
                    <Field
                      name="credential"
                      as={TextField}
                      label="Email"
                      type="email"
                      variant="outlined"
                      fullWidth
                      error={Boolean(errors.credential && touched.credential)}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "white",
                          transition: "transform 0.2s ease-in-out",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                          },
                          "&.Mui-focused": {
                            transform: "translateY(-2px)",
                            boxShadow: "0 4px 12px rgba(255,108,108,0.2)",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#FF6C6C",
                            },
                          },
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#FF6C6C",
                        },
                      }}
                    />
                  )}
                  <ErrorMessage
                    name="credential"
                    component="div"
                    className="ml-3 text-sm italic text-red-500"
                  />
                </motion.div>

                <motion.button
                  type="submit"
                  className="w-full bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Receive OTP
                </motion.button>
              </Form>
            </motion.div>
          )}
        </Formik>

        {/* Feature Cards */}
        <motion.div
          className="grid grid-cols-3 gap-3 mt-12"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.3, duration: 0.8 }}
        >
          <div className="p-3 text-center bg-white rounded-xl shadow-sm transition-shadow duration-300 hover:shadow-md">
            <motion.div whileHover={{ scale: 1.05 }}>
              <div className="text-[#FF6C6C] text-lg mb-1">📱</div>
              <div className="text-xs text-gray-600">Mobile OTP</div>
            </motion.div>
          </div>
          <div className="p-3 text-center bg-white rounded-xl shadow-sm transition-shadow duration-300 hover:shadow-md">
            <motion.div whileHover={{ scale: 1.05 }}>
              <div className="text-[#FF6C6C] text-lg mb-1">⚡</div>
              <div className="text-xs text-gray-600">Quick Reset</div>
            </motion.div>
          </div>
          <div className="p-3 text-center bg-white rounded-xl shadow-sm transition-shadow duration-300 hover:shadow-md">
            <motion.div whileHover={{ scale: 1.05 }}>
              <div className="text-[#FF6C6C] text-lg mb-1">🔒</div>
              <div className="text-xs text-gray-600">Secure Process</div>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default ForgotPassword;
